import RefreshIcon from '@cp/src/assets/icons/RefreshIcon';
import { pluralisation } from '@cp/src/utils/commonUtils';
import { SearchBarInput, TabItem, Tabs } from '@navi/web-ui/lib/components';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import styles from './index.module.scss';
import { TabItemKey, TabsChild } from '@navi/web-ui/lib/components/Tabs/types';
import { useMemo } from 'react';
import {
  APPROVAL_TABS,
  EntityType,
  PENDING_APPROVALS_KEY,
  TAB_KEYS,
  initialPaginationParams
} from './constant';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { createQueryParams, readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { useNavigate } from 'react-router-dom';
import { getPendingApprovals } from './actions/ApprovalsActions';
import { setSelectedIdApprovalItem, setSelectedPanApprovalItem } from './reducers/ApprovalsSlice';
import { debounce } from '@navi/web-ui/lib/utils';
import { ILeftPanel } from './interface';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';

const LeftPanel = (props: ILeftPanel) => {
  const { selectedTabKey, setSelectedTabKey } = props;
  const featureFlags = useSelector((state: RootState) => state?.common?.featureFlags);
  const totalPanPendingApprovals = useSelector(
    (state: RootState) => state.approvals.totalPanPendingApprovals
  );
  const totalIdCardPendingApprovals = useSelector(
    (state: RootState) => state.approvals.totalIdCardPendingApprovals
  );
  const { [PENDING_APPROVALS_KEY]: queryParams } = readQueryParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const totalPendingApprovals =
    selectedTabKey === TAB_KEYS.ID_CARDS ? totalIdCardPendingApprovals : totalPanPendingApprovals;

  const handleTabChange = (tabId: TabItemKey) => {
    setSelectedTabKey(tabId as string);
    if (tabId === TAB_KEYS.ID_CARDS) {
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CLICKED_ID_CARDS);
    } else {
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CLICKED_PAN_CARDS);
    }
    const { [PENDING_APPROVALS_KEY]: queryParams } = readQueryParams();
    const params = { query: queryParams?.query };
    const url = createQueryParams({ [PENDING_APPROVALS_KEY]: params });
    navigate(url);
  };

  const filteredTabs = useMemo(() => {
    const tabs: TabsChild[] = [];
    APPROVAL_TABS.forEach(tab => {
      // Checking if the isVisibleKey flag key is true in featureFlags
      // or by default showing the tab
      const isFeatureFlagEnabled = tab?.featureFlagKey && featureFlags?.[tab.featureFlagKey];
      if (isFeatureFlagEnabled || !tab?.featureFlagKey) {
        tabs.push(
          <TabItem
            key={tab.key}
            label={tab.value}
            tabPanelClass={isFeatureFlagEnabled ? styles.tabPanel : ''}
          >
            <tab.component />
          </TabItem>
        );
      }
    });
    return tabs;
  }, [featureFlags]);

  const fetchPendingApprovals = () => {
    dispatch(
      getPendingApprovals({
        ...initialPaginationParams,
        ...queryParams,
        entityType:
          selectedTabKey === TAB_KEYS.ID_CARDS ? EntityType.AGENT_ID : EntityType.AGENT_PAN_CARD
      })
    );
  };

  const handleSearchQuery = (query: string) => {
    const params = {
      ...initialPaginationParams,
      ...queryParams,
      query,
      page: '1',
      selectedApprovalItem: ''
    };
    const url = createQueryParams({ [PENDING_APPROVALS_KEY]: params });
    navigate(url, {
      replace: true
    });
    dispatch(
      getPendingApprovals({
        ...params,
        entityType:
          selectedTabKey === TAB_KEYS.ID_CARDS ? EntityType.AGENT_ID : EntityType.AGENT_PAN_CARD
      })
    );
    if (selectedTabKey === TAB_KEYS.ID_CARDS) {
      dispatch(setSelectedIdApprovalItem(null));
    } else {
      dispatch(setSelectedPanApprovalItem(null));
    }
  };

  const debounceSearchHandler = debounce(handleSearchQuery, 300);

  return (
    <div className={styles.leftPanel}>
      <div className={styles.header}>
        <div className="flex justify-between mb-3">
          <Typography variant={'h4'}>
            {pluralisation('Pending approval', totalPendingApprovals, 's')}{' '}
            {totalPendingApprovals ? `(${totalPendingApprovals})` : ''}
          </Typography>
          <Button onClick={fetchPendingApprovals} startAdornment={<RefreshIcon />} variant="text">
            Refresh
          </Button>
        </div>
        <SearchBarInput onSearchChange={debounceSearchHandler} value={queryParams?.query || ''} />
      </div>
      <Tabs
        selectedTabKey={selectedTabKey}
        onTabChange={handleTabChange}
        contentClassName={styles.tabContent}
        tabsClassName={styles.tabContainerClass}
      >
        {filteredTabs}
      </Tabs>
    </div>
  );
};

export default LeftPanel;
